<template>
  <BaseModal
    :is-visible="isVisible"
    title="Sync Module Time Settings"
    subtitle="Automatically set up time schedules for all active modules"
    :icon="RefreshCw"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="handleClose"
  >
    <!-- Form Content -->
    <div class="space-y-6">
      <form @submit.prevent="handleSubmit" class="space-y-6">
        <!-- Date Selection Section -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Start Month -->
          <BaseInput
            v-model="form.start_month"
            label="Start Month"
            type="select"
            :options="months"
            :required="true"
          />

          <!-- Start Year -->
          <BaseInput
            v-model="form.start_year"
            label="Start Year"
            type="select"
            :options="yearOptions"
            :required="true"
          />
        </div>

        <!-- Two-Column Layout for Module Order and Preview -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- Module Order Section -->
          <div class="form-group">
            <div class="flex items-center justify-between mb-3">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Module Order for Sync
              </label>
              <BaseButton
                text="Reset"
                variant="ghost"
                size="sm"
                :icon="RotateCcw"
                :loading="loadingModules"
                loading-text="Loading..."
                @click="loadAvailableModules"
              />
            </div>
            <p class="text-xs text-gray-500 dark:text-gray-400 mb-4">
              Modules are ordered by their assigned time settings. Drag and drop to reorder if needed. The first module will be assigned to the start month, second to the next month, etc.
            </p>
            
            <!-- Module List -->
            <div v-if="availableModules.length > 0" class="max-h-96 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-xl p-3 bg-gray-50 dark:bg-gray-700/50">
              <VueDraggable
                v-model="availableModules"
                :animation="200"
                :delay="isMobile ? 600 : 0"
                :delayOnTouchStart="true"
                ghostClass="ghost"
                chosenClass="chosen"
                dragClass="drag"
                class="space-y-2"
              >
                <div
                  v-for="(module, index) in availableModules"
                  :key="module.module_id"
                  class="module-item group cursor-move p-3 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-500 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:border-blue-300 dark:hover:border-blue-400"
                >
                  <div class="flex items-center gap-3">
                    <!-- Drag Handle -->
                    <div class="flex-shrink-0 text-gray-400 dark:text-gray-500 group-hover:text-blue-500 dark:group-hover:text-blue-400 transition-colors">
                      <GripVertical class="h-5 w-5" />
                    </div>
                    
                    <!-- Module Number -->
                    <div class="flex-shrink-0 w-8 h-8 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full flex items-center justify-center text-sm font-semibold">
                      {{ index + 1 }}
                    </div>
                    
                    <!-- Module Info -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2">
                        <p class="text-sm font-medium text-gray-900 dark:text-white truncate" :title="module.name">
                          {{ module.name }}
                        </p>
                        <span 
                          v-if="module.time_setting" 
                          class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300"
                          :title="`Scheduled: ${formatModuleDateRange(module.time_setting.start_date, module.time_setting.end_date)}`"
                        >
                          <Calendar class="h-4 w-4" />
                        </span>
                      </div>
                      <p v-if="module.description" class="text-xs text-gray-500 dark:text-gray-400 truncate" :title="module.description">
                        {{ module.description }}
                      </p>
                      <p v-if="module.time_setting" class="text-xs text-blue-600 dark:text-blue-400 truncate mt-1" :title="formatModuleDateRange(module.time_setting.start_date, module.time_setting.end_date)">
                        {{ formatModuleDateRange(module.time_setting.start_date, module.time_setting.end_date) }}
                      </p>
                    </div>
                    
                    <!-- Status Badge -->
                    <div class="flex-shrink-0">
                      <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200">
                        Active
                      </span>
                    </div>
                  </div>
                </div>
              </VueDraggable>
            </div>
            
            <!-- Loading/Empty State -->
            <div v-else class="p-6 text-center border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50">
              <div v-if="loadingModules" class="flex items-center justify-center text-gray-500 dark:text-gray-400">
                <Loader2 class="h-5 w-5 animate-spin mr-2" />
                Loading available modules...
              </div>
              <div v-else class="text-gray-500 dark:text-gray-400">
                <Database class="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p class="text-sm">No active modules found</p>
                <p class="text-xs mt-1">Click refresh to load modules</p>
              </div>
            </div>
          </div>

          <!-- Live Preview Section -->
          <div class="form-group mt-8">
            <div class="flex items-center gap-2 mt-8 mb-6">
              <label class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                Live Preview
              </label>
              <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                <span class="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                Auto-updates
              </span>
            </div>
            
            <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl h-96 overflow-hidden flex flex-col">
              <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-3 flex items-center gap-2">
                <Eye v-if="!previewing" class="h-4 w-4" />
                <Loader2 v-else class="h-4 w-4 animate-spin" />
                <span>Sync Preview</span>
                <span v-if="previewing" class="text-xs opacity-75">(Updating...)</span>
              </h4>
              
              <div v-if="previewResults" class="text-sm mb-3 p-3 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                <div class="flex flex-wrap gap-4 text-blue-700 dark:text-blue-300">
                  <div class="flex items-center gap-1">
                    <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
                    <strong>Total Modules:</strong> 
                    <span class="font-bold text-blue-800 dark:text-blue-200">{{ previewResults.total_modules }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span class="w-2 h-2 bg-emerald-500 rounded-full"></span>
                    <strong>Starting From:</strong> 
                    <span class="font-bold text-emerald-800 dark:text-emerald-200">{{ previewResults.start_month }}</span>
                  </div>
                </div>
              </div>
              
              <div v-if="!previewResults && !previewing" class="text-sm text-gray-500 dark:text-gray-400 text-center py-8 bg-gray-50 dark:bg-gray-800/30 rounded-lg border border-gray-200 dark:border-gray-600">
                <Eye class="h-6 w-6 mx-auto mb-2 opacity-50" />
                <p class="font-medium">Preview will appear here</p>
                <p class="text-xs mt-1">Configure settings to see module assignments</p>
              </div>
              
              <div v-if="previewData.length > 0" class="flex-1 flex flex-col overflow-hidden">
                <div class="flex items-center gap-2 mb-2">
                  <span class="text-xs text-blue-600 dark:text-blue-400 font-medium">Module assignments preview:</span>
                </div>
                <div class="flex-1 overflow-y-auto border border-blue-200 dark:border-blue-700 rounded-lg p-2 pb-3 bg-white dark:bg-gray-800/50">
                  <div class="space-y-1">
                    <div
                      v-for="(setting, index) in previewData.slice(0, 44)"
                      :key="index"
                      :class="[
                            'text-xs px-3 py-2 rounded-md transition-colors duration-200 ease-in-out',
                            getPreviewItemClasses(index)
                          ]"
                    >
                      <div class="flex items-center gap-2 w-full">
                        <span class="font-medium truncate flex-1 min-w-0" :title="setting.module_name">{{ setting.module_name }}</span>
                        <span class="text-xs opacity-80 ml-2 whitespace-nowrap flex-shrink-0" :title="setting.month_year">{{ setting.month_year }}</span>
                      </div>
                    </div>
                    <div v-if="previewData.length > 44" class="text-xs text-blue-600 dark:text-blue-400 text-center py-2 italic border-t border-blue-200 dark:border-blue-700 mt-2 pt-2">
                      ... and more assignments ...
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
          <div class="flex items-start gap-3">
            <AlertCircle class="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
            <div>
              <h4 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h4>
              <p class="text-sm text-red-700 dark:text-red-300 mt-1">{{ error }}</p>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        :disabled="loading"
        @click="handleClose"
      />
      <BaseButton
        text="Sync"
        variant="primary"
        :icon="RefreshCw"
        :loading="syncing"
        loading-text="Syncing..."
        :disabled="loading"
        @click="handleSubmit"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { Eye, RefreshCw, AlertCircle, Loader2, GripVertical, RotateCcw, Database, Calendar } from 'lucide-vue-next'
import { VueDraggable } from 'vue-draggable-plus'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import BaseInput from '@/components/common/BaseInput.vue'
import { useResponsive } from '@/composables/ui/useResponsive'
import ModulesService, { type CalibratePayload, type CalibrateResponse, type Module } from '@/services/modules'

interface Props {
  isVisible: boolean
}

interface Emits {
  close: []
  success: [message: string]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Responsive utilities for mobile detection
const { isMobile } = useResponsive()

// Current year for validation
const currentYear = new Date().getFullYear()

// Form state - Set cycles to 10 for indefinite cycles
const form = ref<CalibratePayload>({
  start_month: 1, // January
  start_year: currentYear,
  cycles: 10 // Standard for indefinite cycles
})

// Loading states
const loading = computed(() => syncing.value || previewing.value)
const syncing = ref(false)
const previewing = ref(false)
const loadingModules = ref(false)

// Error and preview states
const error = ref<string | null>(null)
const previewResults = ref<CalibrateResponse | null>(null)

// Module management
const availableModules = ref<Module[]>([])

// Month options for BaseInput select
const months = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
]

// Year options for BaseInput select - from current year to 10 years in the future
const yearOptions = Array.from({ length: 11 }, (_, i) => ({
  value: currentYear + i,
  label: (currentYear + i).toString()
}))

// Computed preview data
const previewData = computed(() => {
  if (!previewResults.value) return []
  return previewResults.value.preview || previewResults.value.settings || []
})

// Load available active modules
const loadAvailableModules = async () => {
  try {
    loadingModules.value = true
    error.value = null
    
    const modules = await ModulesService.getActiveModules()
    
    // Sort modules by time settings first (if they exist), then by module_id
    availableModules.value = modules.sort((a, b) => {
      // If both modules have time settings, sort by start date
      if (a.time_setting && b.time_setting) {
        const aStartDate = new Date(a.time_setting.start_date).getTime()
        const bStartDate = new Date(b.time_setting.start_date).getTime()
        
        if (aStartDate !== bStartDate) {
          return aStartDate - bStartDate // Ascending order by start date
        } else {
          // If start dates are the same, fall back to module_id
          return a.module_id - b.module_id
        }
      }
      
      // Modules with time settings come first
      if (a.time_setting && !b.time_setting) {
        return -1
      }
      if (!a.time_setting && b.time_setting) {
        return 1
      }
      
      // If both don't have time settings, sort by module_id
      return a.module_id - b.module_id
    })
    
    // Auto-load preview once modules are loaded
    if (props.isVisible && availableModules.value.length > 0) {
      // Small delay to ensure DOM updates
      setTimeout(() => {
        handlePreview()
      }, 300)
    }
  } catch (err: any) {
    error.value = err.message || 'Failed to load modules'
    availableModules.value = []
  } finally {
    loadingModules.value = false
  }
}

// Load modules when modal opens
onMounted(() => {
  loadAvailableModules()
})

// Reset form when modal is opened
watch(() => props.isVisible, (isVisible) => {
  if (isVisible) {
    form.value = {
      start_month: 1,
      start_year: currentYear,
      cycles: 50 // Standard for indefinite cycles
    }
    error.value = null
    previewResults.value = null
    loadAvailableModules()
  } else {
    // Clear timeout when modal closes
    if (previewTimeout) {
      clearTimeout(previewTimeout)
      previewTimeout = null
    }
  }
})

// Auto-update preview when module order changes
let previewTimeout: number | null = null

watch(
  () => availableModules.value.map(module => module.module_id),
  async (newOrder, oldOrder) => {
    // Clear existing timeout
    if (previewTimeout) {
      clearTimeout(previewTimeout)
    }

    // Only trigger if modules are loaded and order actually changed
    if (
      props.isVisible && 
      availableModules.value.length > 0 && 
      newOrder.length === oldOrder?.length &&
      JSON.stringify(newOrder) !== JSON.stringify(oldOrder)
    ) {
      // Debounce the preview update by 800ms to avoid too many API calls
      previewTimeout = setTimeout(async () => {
        await handlePreview()
      }, 800)
    }
  },
  { deep: true }
)

// Auto-update preview when form settings change
watch(
  () => ({ ...form.value }),
  async (newForm, oldForm) => {
    // Clear existing timeout
    if (previewTimeout) {
      clearTimeout(previewTimeout)
    }

    // Only trigger if form actually changed
    if (
      props.isVisible && 
      availableModules.value.length > 0 && 
      JSON.stringify(newForm) !== JSON.stringify(oldForm)
    ) {
      // Debounce the preview update by 1000ms for form changes
      previewTimeout = setTimeout(async () => {
        await handlePreview()
      }, 1000)
    }
  },
  { deep: true }
)

// Helper function to format date range for modules
const formatModuleDateRange = (startDate: string, endDate: string) => {
  const start = new Date(startDate).toLocaleDateString()
  const end = new Date(endDate).toLocaleDateString()
  return `${start} - ${end}`
}

// Helper function to get dynamic classes for preview items
const getPreviewItemClasses = (index: number) => {
  // Default neutral styles
  const neutral = 'bg-transparent dark:bg-transparent text-slate-700 dark:text-slate-300 rounded-md border-l-4'

  // Permanent border colors per scheme (visible even when not hovered)
  const borderSchemes = [
    'border-blue-500 dark:border-blue-400',
    'border-emerald-500 dark:border-emerald-400',
    'border-purple-500 dark:border-purple-400',
    'border-orange-500 dark:border-orange-400'
  ]

  // Hover-only background and text accents
  const hoverAccents = [
    'hover:bg-blue-100 hover:dark:bg-blue-900/40 hover:text-blue-900 hover:dark:text-blue-100',
    'hover:bg-emerald-100 hover:dark:bg-emerald-900/40 hover:text-emerald-900 hover:dark:text-emerald-100',
    'hover:bg-purple-100 hover:dark:bg-purple-900/40 hover:text-purple-900 hover:dark:text-purple-100',
    'hover:bg-orange-100 hover:dark:bg-orange-900/40 hover:text-orange-900 hover:dark:text-orange-100'
  ]

  const i = index % 4

  return `${neutral} ${borderSchemes[i]} ${hoverAccents[i]}`
}

const handleClose = () => {
  if (!loading.value) {
    emit('close')
  }
}

const handlePreview = async () => {
  if (previewing.value) return

  try {
    previewing.value = true
    error.value = null
    
    // Create payload with custom module order
    const payload: CalibratePayload = {
      ...form.value,
      module_order: availableModules.value.map(module => module.module_id)
    }
    
    const response = await ModulesService.previewCalibration(payload)
    previewResults.value = response.data
  } catch (err: any) {
    error.value = err.message || 'Failed to preview calibration'
    previewResults.value = null
  } finally {
    previewing.value = false
  }
}

const handleSubmit = async () => {
  if (syncing.value) return

  try {
    syncing.value = true
    error.value = null
    
    // Create payload with custom module order
    const payload: CalibratePayload = {
      ...form.value,
      module_order: availableModules.value.map(module => module.module_id)
    }
    
    const response = await ModulesService.calibrateModules(payload)
    emit('success', response.message || 'Module time settings synced successfully')
    emit('close')
  } catch (err: any) {
    error.value = err.message || 'Failed to sync modules'
  } finally {
    syncing.value = false
  }
}
</script>

<style scoped>
/* Drag and drop styling */
.module-item {
  position: relative;
  transition: all 0.2s ease;
}

.module-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.module-item.dragging {
  opacity: 0.7;
  transform: scale(1.02);
  z-index: 1000;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.module-item.drag-over {
  border-color: #3b82f6;
  background-color: #eff6ff;
  transform: scale(1.02);
}

.dark .module-item.drag-over {
  border-color: #60a5fa;
  background-color: rgba(59, 130, 246, 0.1);
}

/* Vue Draggable Styles */
.ghost {
  opacity: 0.4;
  background: #f0f9ff;
  border: 2px dashed #3b82f6;
  transform: scale(0.98);
}

.chosen {
  transform: scale(1.03);
  box-shadow: 0 8px 20px rgba(59, 130, 246, 0.2);
  border-color: #3b82f6;
}

.drag {
  opacity: 0.8;
  transform: scale(1.05);
  z-index: 9999;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
}

/* Preview item base styling overrides for hover-only colors */
.module-item .text-xs.px-3.py-2.rounded-md {
  transition: background-color 180ms ease-in-out, color 180ms ease-in-out, border-color 180ms ease-in-out;
}

.dark .module-item .text-xs.px-3.py-2.rounded-md:hover {
  /* In dark mode, allow subtle tinted backgrounds on hover */
  background-color: rgba(255, 255, 255, 0.02);
}
</style>
